import React, { useEffect, useState } from "react";

export interface TocEntry {
  id: string;
  label: string;
}

/**
 * Sticky in-page navigation for the long-form pages. Highlights the section
 * currently in view; falls back to the first entry before any section is
 * intersecting (top of page).
 */
export const SideNav: React.FC<{ entries: TocEntry[]; heading?: string }> = ({
  entries,
  heading = "On this page",
}) => {
  const [active, setActive] = useState(entries[0]?.id ?? "");

  useEffect(() => {
    const nodes = entries
      .map((e) => document.getElementById(e.id))
      .filter((n): n is HTMLElement => n !== null);

    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (records) => {
        const visible = records
          .filter((r) => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActive(visible[0].target.id);
        }
      },
      // Band just below the sticky header: the section crossing it is "current".
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [entries]);

  return (
    <nav aria-label={heading} className="text-sm">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink-400">
        {heading}
      </p>
      <ul className="space-y-0.5 border-l border-sand-200">
        {entries.map((entry) => {
          const isActive = entry.id === active;
          return (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                className={`-ml-px block border-l-2 py-1.5 pl-4 leading-snug transition ${
                  isActive
                    ? "border-accent font-semibold text-ink"
                    : "border-transparent text-ink-400 hover:border-sand-300 hover:text-ink-600"
                }`}
              >
                {entry.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/**
 * Long-form page scaffold: intro band, then content with a sticky sidebar.
 */
export const LongPage: React.FC<{
  intro: React.ReactNode;
  toc: TocEntry[];
  children: React.ReactNode;
}> = ({ intro, toc, children }) => (
  <>
    <div className="border-b border-sand-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">{intro}</div>
    </div>
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="gap-14 py-14 lg:flex">
        <aside className="mb-10 shrink-0 lg:mb-0 lg:w-56">
          <div className="lg:sticky lg:top-28">
            <SideNav entries={toc} />
          </div>
        </aside>
        <div className="min-w-0 max-w-3xl flex-1">{children}</div>
      </div>
    </div>
  </>
);
